import type { MutableState } from 'final-form';
import type { RefObject } from 'react';

import type { TFormState } from '../models';

const SUBMIT_BUTTON = 'button[type=submit]';

const focusOnSubmit = (formRef: RefObject<HTMLFormElement>) =>
    formRef.current?.querySelector<HTMLButtonElement>(SUBMIT_BUTTON)?.focus();

/**
 * Создает новый массив, в нем первый элемент - первый после текущего,
 *  остальные смещаются в конец, текущий удаляется.
 */
const shiftElements = (elements: string[], newStart: number) => {
    if (elements.length < 2) return elements;
    const newElements: Array<string | undefined> = Array.from(elements);

    for (let index = 0; index <= newStart; index += 1) {
        newElements.push(newElements.shift());
    }

    newElements.pop();

    return newElements;
};

type TType = Partial<Record<string, unknown>>;

const focusOnElement = (current: HTMLInputElement | null) => {
    if (!current) return;

    const length = current.value?.length || 0;
    current.focus?.();
    // Всегда ставить фокус в конец заполненной строки
    if (current.setSelectionRange) current.setSelectionRange(length, length);
};

const getCurrent = ({ fields }: MutableState<TType>, name?: string) =>
    name && fields[name].data.ref.current;

const findNextFocus = (
    { errors, values }: TFormState,
    fields: MutableState<TType>['fields'],
    elements: string[],
    name?: string
) =>
    shiftElements(
        elements,
        elements.findIndex(val => val === name)
    ).find(
        elementName =>
            (errors[elementName as string] || !values[elementName as string]) &&
            fields[elementName as string]?.data.receivesFocus
    );

export const onComplete =
    (formRef: RefObject<HTMLFormElement>, elements: string[]) =>
    ([name]: [string | undefined], state: MutableState<Record<string, unknown>>) => {
        if (!state.lastFormState) return;
        const { hasValidationErrors, pristine } = state.lastFormState;

        if (hasValidationErrors || pristine) {
            const next = findNextFocus(state.lastFormState, state.fields, elements, name);
            if (!next) return;
            focusOnElement(getCurrent(state, next));
        } else {
            focusOnSubmit(formRef);
        }
    };
